import React, { useEffect, useRef, useState } from 'react'
import { Card, CardBody, Col, Row, Table, Button, Badge, Modal, ModalBody, Label, Input, FormGroup } from 'reactstrap';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Skeleton from "react-loading-skeleton";
import AddCardModal from './AddCardModal';
import CurrentSubsPlan from "../../assets/images/current-subs-plan.png"
import MinutesUsed from "../../assets/images/mins-used.png"
import NextRenewal from "../../assets/images/next-renewal.png"
import AvailableBalance from "../../assets/images/available-balance.png"
import CurrentSpend from "../../assets/images/current-spend.png"
import AutoRecharge from "../../assets/images/auto-recharge.png"
import { addFunds, autoTopup, cancelSubscription, getBillingDetails, getInvoices, getPaymentMethods, setPrimaryCard, removeCard } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { EmptyRow } from '../../components/EmptyState';
import editIcon from "../../assets/images/edit-icon.png"
import moment from 'moment/moment';
import { useAppContext } from '../../context/AppContext';
import { Link } from 'react-router-dom';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const CARD_BRAND_STYLES = {
    visa: { label: "VISA", bg: "#1A1F71", color: "#fff" },
    mastercard: { label: "MC", bg: "#EB001B", color: "#fff" },
    amex: { label: "AMEX", bg: "#2E77BC", color: "#fff" },
    american_express: { label: "AMEX", bg: "#2E77BC", color: "#fff" },
    discover: { label: "DISCOVER", bg: "#FF6000", color: "#fff" },
    diners: { label: "DINERS", bg: "#0079BE", color: "#fff" },
    jcb: { label: "JCB", bg: "#0B4EA2", color: "#fff" },
    unionpay: { label: "UNIONPAY", bg: "#E21836", color: "#fff" },
};

const CardBrandLogo = ({ brand }) => {
    const key = (brand || "").toLowerCase();

    const style = CARD_BRAND_STYLES[key];
    if (style) {
        return (
            <span
                style={{ background: style.bg, color: style.color, fontSize: "11px", fontWeight: 700, padding: "4px 8px", borderRadius: "4px", letterSpacing: "0.5px" }}
            >
                {style.label}
            </span>
        );
    }

    return <i className="ri-bank-card-2-line fs-22 text-muted"></i>;
};

const Billing = () => {
    const { isTrial, setIsActiveSubscription, isActiveSubscription } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [billingDetails, setBillinDetails] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [cancelModal, setCancelModal] = useState(false);
    const [autoRechargeModal, setAutoRechargeModal] = useState(false);
    const [autoRecharge, setAutoRecharge] = useState(true);
    const [rechargeAmount, setRechargeAmount] = useState("");
    const [fundAmount, setFundAmount] = useState("");
    const [addFundsModal, setAddFundsModal] = useState(false);
    const [addCardModal, setAddCardModal] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [deleteCardId, setDeleteCardId] = useState(null);
    const [update, setUpdate] = useState(0);
    const didFetchRef  = useRef(false);

    useEffect(() => {
        if (didFetchRef.current) return;

        if(isTrial) return;

        didFetchRef.current = true;

        if (!isTrial) {
            getBillingHandler();
            getInvoicesHandler();
            getPaymentMethodsHandler();
        }
    }, [isTrial]);

    const getBillingHandler = () => {
        setLoading(true)
        setBillinDetails({})
        getBillingDetails().then((data) => {
            if (data.data) {
                setBillinDetails(data.data)
                setAutoRecharge(data.data.autoRecharge)
                setRechargeAmount(data.data.topUpAmount / 100)
            }
            else {
                showToast("error", "Request Failed", data.message)
            }
            setLoading(false)
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }
    const getPaymentMethodsHandler = () => {
        setLoading(true)
        setPaymentMethods([])
        getPaymentMethods().then(({ data }) => {
            if (data.data) {
                setPaymentMethods(Array.isArray(data.data) ? data.data : [])
            }
            else {
                showToast("error", "Request Failed", data.message)
            }
            setLoading(false)
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }
    const setPrimaryCardHandler = (paymentMethodId) => {
        setLoading(true)
        setPrimaryCard({ paymentMethodId }).then((data) => {
            if (data.data?.success) {
                showToast("success", "Primary Card Updated", "Primary card updated successfully.")
                getPaymentMethodsHandler();
            }
            else {
                showToast("error", "Request Failed", data.data?.message || data.message)
                setLoading(false)
            }
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }
    const removeCardHandler = async () => {
        if (paymentMethods.length <= 1) {
            showToast("error", "Action Not Allowed", "You must have at least one payment method on file.")
            setDeleteCardId(null)
            return;
        }

        const cardToDelete = paymentMethods.find((method) => method.paymentMethodId === deleteCardId);
        if (!cardToDelete) return;

        setLoading(true)
        try {
            if (cardToDelete.isPrimary) {
                const nextPrimary = paymentMethods.find((method) => method.paymentMethodId !== deleteCardId);
                const primaryRes = await setPrimaryCard({ paymentMethodId: nextPrimary.paymentMethodId });
                if (!primaryRes.data?.success) {
                    showToast("error", "Request Failed", primaryRes.data?.message || "Unable to update primary card.")
                    setLoading(false)
                    return;
                }
            }

            const { data } = await removeCard(deleteCardId);
            if (data?.success) {
                showToast("success", "Card Removed", "Card removed successfully.")
                setDeleteCardId(null)
                getPaymentMethodsHandler();
            }
            else {
                showToast("error", "Request Failed", data?.message || "Unable to remove card.")
                setLoading(false)
            }
        } catch (err) {
            showToast("error", "Request Failed", err.response?.data?.message || err.message)
            setLoading(false)
        }
    }
    const getInvoicesHandler = () => {
        setLoading(true)
        setInvoices([])
        getInvoices().then((data) => {
            console.log("receipts", data)
            if (data.status === 200) {
                setInvoices(data.data.receipts ? data.data.receipts : [])
            }
            else {
                showToast("error", "Request Failed", data.message)
            }
            setLoading(false)
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }
    const cancelSubscriptionHandler = () => {
        setLoading(true)
        cancelSubscription(billingDetails.subscriptionId).then((data) => {
            if (data.data) {
                showToast("success", "Subscription Canceled", "Subcription cancelled successfully.")
                setCancelModal(false)
                setIsActiveSubscription(false)
                setUpdate(prev => prev + 1)
                getBillingHandler();
                getInvoicesHandler();
            }
            else {
                showToast("error", "Request Failed", data.message)
            }
            setLoading(false)
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }
    const addFundsHandler = () => {
        setLoading(true)
        addFunds({ amount: Number(fundAmount) }).then((data) => {
            if (data.data.success) {
                console.log(data)
                showToast("success", "Funds Added", `$${fundAmount} added to your account successfully.`)
                setAddFundsModal(false)
                setFundAmount("")
                setUpdate(prev => prev + 1)
                getBillingHandler();
                getInvoicesHandler();
            }
            else {
                showToast("error", "Request Failed", data.message)
            }
            setLoading(false)
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }
    const autoTopupHandler = () => {
        setLoading(true)
        autoTopup({ autoTopUp: autoRecharge, ...(autoRecharge && { topUpAmount: Number(rechargeAmount) }) }).then((data) => {
            if (data.data.success) {
                showToast("success", "Status Updated", `Topup status updated successfully.`)
                setAutoRechargeModal(false)
                setUpdate(prev => prev + 1)
                getBillingHandler();
                getInvoicesHandler();
            }
            else {
                showToast("error", "Request Failed", data.message)
            }
            setLoading(false)
        }).catch((err) => {
            showToast("error", "Request Failed", err.message)
            setLoading(false)
        })
    }

    const formatSecondsToMinSec = (seconds) => {
        if (!seconds || seconds <= 0) return "0s";
        
        const duration = moment.duration(seconds, 'seconds');
        const minutes = Math.floor(duration.asMinutes());
        const remainingSeconds = Math.floor(duration.asSeconds() % 60);
        
        if (minutes === 0) {
          return `${remainingSeconds}s`;
        } else if (remainingSeconds === 0) {
          return `${minutes}m`;
        } else {
          return `${minutes}m ${remainingSeconds}s`;
        }
      };

    return (
        <Elements stripe={stripePromise}>
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="py-3 row">
                        <div className="col-12">
                            <Row className="my-2 align-items-center">
                                <Col md={6}>
                                    <h4>Billing Details</h4>
                                </Col>
                                <Col md={6} className='text-right'>
                                    {
                                        isTrial ? null
                                            :
                                            billingDetails.subscriptionStatus === "active" ?
                                                <Button color='outline-danger' className='py-2 rounded bg-white cancel-subscription' onClick={() => setCancelModal(true)}>Cancel Subscription</Button> : null
                                    }
                                </Col>
                            </Row>
                        </div>
                    </div>
                    {
                        isTrial ?
                            <>
                                <div className='info-box warning'>
                                    <p className='m-0 text-dark d-flex justify-content-between align-items-center'><span><i className="ri-information-line fs-16"></i> Please select a plan to view billing details</span> <Link id='create-agent-btn' to={"/pricing-plans"} className="font-normal py-2 px-4 rounded fs-16 text-decoration-none text-white linear-gradient-btn">Select a Plan</Link> </p>
                                </div>
                            </>
                            :
                            <>
                                <Row>
                                    <Col md={4}>
                                        {
                                            loading ?
                                                <Skeleton height={150} />
                                                :
                                                <Card>
                                                    <CardBody>
                                                        <div className="widget-first">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <img src={CurrentSubsPlan} alt="Current Subscription Plan" className='me-2' />
                                                                <p className="mb-0 text-dark fs-15 fw-600">Current Subscription Plan</p>
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h3 className="mt-2 fs-22 text-dark me-3">{billingDetails?.planName ?? "N/A"}</h3>
                                                            </div>

                                                        </div>
                                                    </CardBody>
                                                </Card>
                                        }
                                    </Col>
                                    <Col md={4}>
                                        {
                                            loading ?
                                                <Skeleton height={150} />
                                                :
                                                <Card>
                                                    <CardBody>
                                                        <div className="widget-first">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <img src={MinutesUsed} alt="Minutes Used this cycle" className='me-2' />
                                                                <p className="mb-0 text-dark fs-15 fw-600">Minutes Used This Cycle</p>
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h3 className="mt-2 fs-22 text-dark me-3">{formatSecondsToMinSec(billingDetails?.minutesUsedThisMonth) ?? 0} / {(billingDetails?.minutesUsedThisMonth / 60) + billingDetails?.freeMinutesRemaining ?? 0} Minutes</h3>
                                                            </div>

                                                        </div>
                                                    </CardBody>
                                                </Card>
                                        }
                                    </Col>
                                    <Col md={4}>
                                        {
                                            loading ?
                                                <Skeleton height={150} />
                                                :
                                                <Card>
                                                    <CardBody>
                                                        <div className="widget-first">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <img src={NextRenewal} alt="Next Subscription Renewal Date" className='me-2' />
                                                                <p className="mb-0 text-dark fs-15 fw-600">{isActiveSubscription ? "Next Subscription Renewal Date" : "Number Active Until"}</p>
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h3 className="mt-2 fs-22 text-dark me-3">{billingDetails?.nextRenewalDate ? moment.utc(billingDetails.nextRenewalDate).local().format("D MMMM, YYYY") : "N/A"}</h3>
                                                            </div>

                                                        </div>
                                                    </CardBody>
                                                </Card>
                                        }
                                    </Col>
                                    <Col md={12}>
                                        <h4 className='mt-3 mb-4'>Wallet Usage</h4>
                                    </Col>
                                    <Col md={4}>
                                        {
                                            loading ?
                                                <Skeleton height={150} />
                                                :
                                                <Card>
                                                    <CardBody>
                                                        <div className="widget-first">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <img src={AvailableBalance} alt="Available Balance" className='me-2' />
                                                                <p className="mb-0 text-dark fs-15 fw-600">Available Balance</p>
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h3 className="mt-2 fs-22 text-dark me-3">${((billingDetails?.walletBalance ?? 0) / 100).toFixed(2)}</h3>
                                                                <Button disabled={loading || !billingDetails?.planName} onClick={() => setAddFundsModal(true)} className="px-3 py-2 rounded linear-gradient-btn">{loading ? "Please Wait" : "Add Funds"}</Button>
                                                            </div>

                                                        </div>
                                                    </CardBody>
                                                </Card>
                                        }
                                    </Col>
                                    <Col md={4}>
                                        {
                                            loading ?
                                                <Skeleton height={150} />
                                                :
                                                <Card>
                                                    <CardBody>
                                                        <div className="widget-first">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <img src={CurrentSpend} alt="Current month-to-date spend" className='me-2' />
                                                                <div>
                                                                    <p className="mb-0 text-dark fs-15 fw-600">Current month-to-date spend</p>
                                                                    <p className='text-muted fs-14 m-0'>for {moment().format("MMMM YYYY")}</p>
                                                                </div>
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h3 className="mt-2 fs-22 text-dark me-3">${(billingDetails?.monthToDateSpent ?? 0).toFixed(2)}</h3>
                                                            </div>

                                                        </div>
                                                    </CardBody>
                                                </Card>
                                        }
                                    </Col>
                                    <Col md={4}>
                                        {
                                            loading ?
                                                <Skeleton height={150} />
                                                :
                                                <Card>
                                                    <CardBody>
                                                        <div className="widget-first">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <img src={AutoRecharge} alt="Auto Recharge" className='me-2' />
                                                                <div>
                                                                    <p className="mb-0 text-dark fs-15 fw-600">Auto Recharge</p>
                                                                    {
                                                                        billingDetails?.autoRecharge ? <p className='text-muted fs-14 m-0'>Auto top-up ${billingDetails?.topUpAmount / 100 ?? "0"} when balance runs out</p> : null
                                                                    }
                                                                </div>
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h3 className="mt-2 fs-22 text-dark me-3">{billingDetails?.autoRecharge ? "Enabled" : "Disabled"}</h3>
                                                                <Button disabled={loading || !billingDetails?.planName} onClick={() => setAutoRechargeModal(true)} className="px-3 py-2 rounded linear-gradient-btn">{loading ? "Please Wait" : "Change"}</Button>
                                                            </div>

                                                        </div>
                                                    </CardBody>
                                                </Card>
                                        }
                                    </Col>
                                </Row>
                                <Row className="my-3 align-items-center">
                                    <Col md={12} className='mb-3 d-flex align-items-center gap-3'>
                                        <h4 className='m-0'>Payment Method</h4>
                                        <Button disabled={loading} className="px-3 py-2 rounded linear-gradient-btn" onClick={() => {
                                            if (paymentMethods.length >= 3) {
                                                showToast("error", "Limit Reached", "You cannot add more than 3 cards. Please delete one of them to add a new one.")
                                                return;
                                            }
                                            setAddCardModal(true)
                                        }}>Add Card</Button>
                                    </Col>
                                    {
                                        loading ?
                                            <Col md={4}>
                                                <Skeleton height={60} />
                                            </Col>
                                            :
                                            paymentMethods.length === 0 ?
                                                <Col md={12}>
                                                    <p className='text-muted fs-14 m-0'>No card added yet.</p>
                                                </Col>
                                                :
                                                [...paymentMethods].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)).map((method) => (
                                                    <Col md={4} key={method._id}>
                                                        <div className="billing-payment-method-card bg-white">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <CardBrandLogo brand={method.brand} />
                                                                <span className="text-muted fs-16">**** {method.card}</span>
                                                                {
                                                                    method.isPrimary ?
                                                                        <Badge color='badge-success badge-soft-success fs-12'>Primary</Badge> : null
                                                                }
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3">
                                                                {
                                                                    !method.isPrimary ?
                                                                        <span
                                                                            className="fs-14 text-primary cursor-pointer text-nowrap px-3 py-1 rounded-pill border border-primary"
                                                                            onClick={() => !loading && setPrimaryCardHandler(method.paymentMethodId)}
                                                                        >
                                                                            Make Primary
                                                                        </span> : null
                                                                }
                                                                {
                                                                    paymentMethods.length > 1 ?
                                                                        <i
                                                                            className="ri-delete-bin-line cursor-pointer text-danger fs-18"
                                                                            onClick={() => !loading && setDeleteCardId(method.paymentMethodId)}
                                                                        ></i>
                                                                        :
                                                                        <i
                                                                            className="ri-delete-bin-line fs-18 text-muted"
                                                                            style={{ cursor: "not-allowed", opacity: 0.5 }}
                                                                            title="You must have at least one payment method"
                                                                        ></i>
                                                                }
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))
                                    }
                                </Row>
                                <Row className="my-3 align-items-center">
                                    <Col md={12}>
                                        <h4>Invoices</h4>
                                    </Col>
                                    <Col md={12}>
                                        <Table hover responsive striped className='border-rounded'>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th className="text-center">Invoice ID</th>
                                                    <th className="text-center">Description</th>
                                                    <th className="text-center">Amount</th>
                                                    <th className="text-center">Status</th>
                                                    <th className="text-center w-20">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    loading ?
                                                        [...Array(10)].map((a, i) => {
                                                            return <tr key={i}>
                                                                <td className="fs-12"><Skeleton height={12} width={80} /></td>
                                                                <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                                                                <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                                                                <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                                                                <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                                                                <td className="fs-12 text-center w-20"><Skeleton height={12} width={80} /></td>
                                                            </tr>
                                                        }) :
                                                        invoices.length === 0 ?
                                                            <EmptyRow colSpan={6} icon="ri-file-list-3-line" title="No invoices yet" body="Invoices appear here once your first billing period closes." /> :
                                                            invoices.map((receipt, i) => {
                                                                return (
                                                                    <tr key={i}>
                                                                        <td className="fs-12 fw-500">{moment.unix(receipt.created).format('D MMM, YYYY')}</td>
                                                                        <td className="fs-12 fw-500 text-center">{receipt.id}</td>
                                                                        <td className="fs-12 fw-500 text-center">{receipt.description}</td>
                                                                        <td className="fs-12 fw-500 text-center"><span className='text-uppercase'>{receipt.currency}</span> {((receipt?.amount ?? 0) / 100).toFixed(2)}</td>
                                                                        <td className="fs-12 fw-500 text-center text-capitalize">{receipt.status}</td>
                                                                        <td className="fs-12 fw-500 text-center w-20"><Button className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn" onClick={() => window.open(receipt.receiptUrl, '_blank')}><i className="ri-file-download-line"></i> Download PDF</Button></td>
                                                                    </tr>
                                                                )

                                                            })
                                                }
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>
                            </>
                    }

                    <Modal isOpen={cancelModal} toggle={() => { setCancelModal(false) }} centered={true} size='md'>
                        <ModalBody>
                            <Row>
                                <Col md={12} className='text-center'>
                                    <h4 className='fs-30 mt-2'>Cancel Subscription</h4>
                                    <p className='fs-18 text-muted'> Are you sure you want to cancel your plan? <br /> You will still be able to use your phone number and AI agent until <b>{billingDetails?.nextRenewalDate ? moment.utc(billingDetails.nextRenewalDate).local().format("D MMMM, YYYY") : "N/A"}</b>, after which your phone number will be released. If you cancel now, you may not be able to get the same phone number if you subscribe to a new plan later.</p>
                                </Col>
                            </Row>
                            <Row className="text-center mt-3">
                                <Col>
                                    <button disabled={loading} onClick={() => { setCancelModal(false) }} className="px-2 py-2 rounded outline-gradient-btn w-100" >
                                        <span>Keep My Subscription</span>
                                    </button>
                                </Col>
                                <Col>
                                    <Button className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn w-100" onClick={cancelSubscriptionHandler}>Yes, Cancel</Button>
                                </Col>
                            </Row>
                        </ModalBody>
                    </Modal>
                    <Modal isOpen={autoRechargeModal} toggle={() => { setAutoRechargeModal(false) }} centered={true} size='md'>
                        <ModalBody>
                            <Row>
                                <Col md={12} className=''>
                                    <div className="text-center">
                                        <h3 className="mt-2 mb-4 fs-26 text-dark">Configure Auto Recharge</h3>
                                    </div>
                                    <Label for="autoRecharge" className="fw-bold mb-3 text-black fs-16">
                                        Auto top-up when balance runs out?
                                    </Label>
                                    <div className="custom-radio-group">
                                        <div className="custom-radio-option">
                                            <Input
                                                type="radio"
                                                name="autoRecharge"
                                                id="autoRechargeNo"
                                                checked={!autoRecharge}
                                                onChange={() => setAutoRecharge(false)}
                                                className="custom-radio-input"
                                            />
                                            <Label for="autoRechargeNo" className="custom-radio-label">
                                                No
                                            </Label>
                                        </div>
                                        <div className="custom-radio-option">
                                            <Input
                                                type="radio"
                                                name="autoRecharge"
                                                id="autoRechargeYes"
                                                checked={autoRecharge}
                                                onChange={() => setAutoRecharge(true)}
                                                className="custom-radio-input"
                                            />
                                            <Label for="autoRechargeYes" className="custom-radio-label">
                                                Yes
                                            </Label>
                                        </div>
                                    </div>
                                    {autoRecharge && (
                                        <FormGroup className="mt-4">
                                            <Label for="rechargeAmount" className="fw-bold mb-2 text-black fs-14">
                                                Recharge balance to
                                            </Label>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <Input
                                                    type="text"
                                                    className='auto-recharge-amount-input'
                                                    id="rechargeAmount"
                                                    value={rechargeAmount}
                                                    onChange={(e) => setRechargeAmount(e.target.value.replace(/\D/g, "").slice(0, 4) || "")}
                                                    min="1"
                                                />
                                            </div>
                                        </FormGroup>
                                    )}
                                </Col>
                            </Row>
                            <Row className="text-center mt-3">
                                <Col>
                                    <button disabled={loading} onClick={() => { setAutoRechargeModal(false) }} className="px-2 py-2 rounded outline-gradient-btn w-100" >
                                        <span>No, Go Back</span>
                                    </button>
                                </Col>
                                <Col>
                                    <Button className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn w-100" onClick={autoTopupHandler} disabled={loading || (autoRecharge && rechargeAmount < 1)}>Yes, Continue</Button>
                                </Col>
                            </Row>
                        </ModalBody>
                    </Modal>
                    <Modal isOpen={addFundsModal} toggle={() => { setAddFundsModal(false) }} centered={true} size='md'>
                        <ModalBody>
                            <Row>
                                <Col md={12} className=''>
                                    <div className="text-center">
                                        <h3 className="mt-2 mb-4 fs-26 text-dark">Add Funds</h3>
                                    </div>
                                    <FormGroup className="mt-4">
                                        <Label for="fundAmount" className="fw-bold mb-2 text-black fs-14">
                                            Enter Amount
                                        </Label>
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <Input
                                                type="text"
                                                className='funds-amount-input'
                                                id="fundAmount"
                                                value={fundAmount}
                                                onChange={(e) => setFundAmount(e.target.value.replace(/\D/g, "").slice(0, 4) || "")}
                                                min="1"
                                            />
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row className="text-center mt-3">
                                <Col>
                                    <button disabled={loading} onClick={() => { setAddFundsModal(false) }} className="px-2 py-2 rounded outline-gradient-btn w-100" >
                                        <span>No, Go Back</span>
                                    </button>
                                </Col>
                                <Col>
                                    <Button className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn w-100" onClick={addFundsHandler} disabled={loading || fundAmount < 1}>Yes, Continue</Button>
                                </Col>
                            </Row>
                        </ModalBody>
                    </Modal>
                    <AddCardModal
                        isOpen={addCardModal}
                        toggle={() => setAddCardModal(false)}
                        onSuccess={() => {
                            setAddCardModal(false);
                            getPaymentMethodsHandler();
                        }}
                    />
                    <Modal isOpen={!!deleteCardId} toggle={() => { setDeleteCardId(null) }} centered={true} size='md'>
                        <ModalBody>
                            <Row>
                                <Col md={12} className='text-center'>
                                    <h4 className='fs-30 mt-2'>Remove Card</h4>
                                    <p className='fs-18 text-muted'>Are you sure you want to remove this card? This action cannot be undone.</p>
                                </Col>
                            </Row>
                            <Row className="text-center mt-3">
                                <Col>
                                    <button disabled={loading} onClick={() => { setDeleteCardId(null) }} className="px-2 py-2 rounded outline-gradient-btn w-100" >
                                        <span>No, Go Back</span>
                                    </button>
                                </Col>
                                <Col>
                                    <Button className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn w-100" onClick={removeCardHandler} disabled={loading}>Yes, Remove</Button>
                                </Col>
                            </Row>
                        </ModalBody>
                    </Modal>
                </div>
            </div>
        </div>
        </Elements>
    )
}

export default Billing
